import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectQuickWidgets } from '../selectors/quickWidgets'
import { selectUserPreferences } from '../selectors/user'
import { QuickWidgetConfig, QuickWidgetTypes, updateUserPreferencesInState } from '../reducer/user.reducer'
import { getQuickWidgetConfigs } from '../util/quickWidgets'

export const useQuickWidgetActions = () => {
    const dispatch = useDispatch()
    const widgets = useSelector(selectQuickWidgets)
    const userPreferences = useSelector(selectUserPreferences)

    const widgetsVisible = useMemo(() => (
        Object.values(widgets || {}).every(w => w?.visible !== false)
    ), [widgets])

    const widgetActive = useMemo(() => (
        !!Object.values(widgets || {}).find((w) => (w as QuickWidgetConfig)?.enabled)
    ), [widgets])

    const handleToggle = useCallback(() => {
        const newValue = !widgetsVisible
        const updatedWidgets = { ...userPreferences.quickWidgets }

        type WidgetKey = keyof typeof updatedWidgets
        ;(Object.keys(updatedWidgets) as WidgetKey[]).forEach((key) => {
            const widget = updatedWidgets[key]
            if (widget) {
                updatedWidgets[key] = {
                    ...widget,
                    visible: newValue
                }
            }
        })

        dispatch(updateUserPreferencesInState({
            ...userPreferences,
            quickWidgets: updatedWidgets
        }))
    }, [dispatch, userPreferences, widgetsVisible])

    const toggleWidget = useCallback((type: QuickWidgetTypes) => {
        if (!widgetsVisible) return
        const updatedWidgets = { ...(userPreferences.quickWidgets || {}) }
        const existing = widgets?.[type] as QuickWidgetConfig
        const isCurrentlyEnabled = !!existing?.enabled
        const newConfig = getQuickWidgetConfigs(type, widgets || {}, true)[type]
        const currentMaxOrder = Math.max(
            0,
            ...Object.values(updatedWidgets)
                .filter((w) => w?.enabled)
                .map((w) => w?.order || 0)
        )
        const updatedWidget: QuickWidgetConfig = {
            ...existing,
            enabled: !isCurrentlyEnabled,
            ...newConfig,
            ...(isCurrentlyEnabled ? {} : { order: currentMaxOrder + 1 })
        }

        dispatch(updateUserPreferencesInState({
            ...userPreferences,
            quickWidgets: {
                ...(userPreferences.quickWidgets || {}),
                [type]: updatedWidget
            }
        }))
    }, [widgetsVisible, userPreferences, widgets, dispatch])

    return {
        widgets,
        widgetsVisible,
        widgetActive,
        handleToggle,
        toggleWidget
    }
}
